<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Badge Component
      p.text--secondary A compact, inline element used to display status, labels, or counts. Available in small (sm) and medium (md) sizes with customizable colors and styling.

    // Basic Badge Examples
    .component-section.mb-8#basic-badges
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-badges')") Basic Badge Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2
              yz-badge(v-bind="variation.props") {{ variation.content }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Size Examples
    .component-section.mb-8#size-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('size-examples')") Size Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in sizeVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2
              yz-badge(v-bind="variation.props") {{ variation.content }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Color Examples
    .component-section.mb-8#color-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-examples')") Color Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in colorVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2.flex-wrap
              yz-badge(v-for="badge in variation.badges" :key="badge.label" v-bind="badge.props") {{ badge.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Height Examples
    .component-section.mb-8#height-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('height-examples')") Height Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in heightVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2.flex-wrap
              yz-badge(v-for="badge in variation.badges" :key="badge.label" v-bind="badge.props") {{ badge.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Border Examples
    .component-section.mb-8#border-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('border-examples')") Border Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in borderVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2
              yz-badge(v-bind="variation.props") {{ variation.content }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Closable Examples
    .component-section.mb-8#closable-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('closable-examples')") Closable Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in closableVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2.flex-wrap
              yz-badge(v-for="(badge, index) in variation.badges" :key="`${badge.label}-${index}`" v-bind="badge.props" @close="handleBadgeClose(badge.label)") {{ badge.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Styling Examples
    .component-section.mb-8#styling-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('styling-examples')") Styling Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in stylingVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation
            .d-flex.align-center.gap-2
              yz-badge(v-bind="variation.props" :class="variation.id === 'custom-padding' ? 'px-5 py-2' : ''") {{ variation.content }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Slot Examples
    .component-section.mb-8#slot-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('slot-examples')") Slot Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in slotVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Badge Implementation with Slots
            .d-flex.align-center.gap-2.flex-wrap
              yz-badge(v-for="badge in variation.badges" :key="badge.id" v-bind="badge.props")
                template(v-if="badge.slotType === 'icon'")
                  yz-icon(size="12" :color="badge.iconColor") {{ badge.icon }}
                template(v-if="badge.slotType === 'dot'")
                  yz-dot(:color="badge.dotColor" :size="badge.dotSize")
                template(v-if="badge.slotType === 'mixed'")
                  yz-dot(:color="badge.dotColor" :size="badge.dotSize")
                  span.ml-1 {{ badge.text }}
                template(v-if="badge.slotType === 'text'")
                  | {{ badge.text }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
      
      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
      
      h4.mb-3 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Alignment: 
              | Badges use inline-block display with vertical-align: middle for proper alignment
            li
              strong Size: 
              | Use 'sm' (20px height) for compact spaces, 'md' (24px height) for more prominent display
            li
              strong Colors: 
              | Supports primary, custom hex colors, and design tokens (yz-color-name format)
            li
              strong Smart Padding: 
              | YzBadge uses smart padding - detects if parent passes spacing classes (px-, py-, pa-, etc.) and omits default padding to prevent conflicts. Default padding is 2px 4px (px-1) for both sm and md sizes
            li
              strong Content: 
              | Keep text concise - badges work best with 1-3 words or numbers
            li
              strong Height: 
              | Custom height prop overrides size-based heights. Numbers are converted to px. Use with padding for proper spacing
            li
              strong Closable: 
              | Use for tag management, filters, or removable items. Always handle the @close event to update your data
            li
              strong Accessibility: 
              | Ensure sufficient color contrast and consider screen reader context. Closable badges include accessible close buttons
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Default Badge',
          id: 'default-badge',
          description: 'Basic badge with default primary color and small size',
          label: 'Default Badge',
          hint: 'Primary color, small size',
          props: {},
          content: 'New',
          code: `yz-badge New`
        },
        {
          name: 'Custom Height Badge',
          id: 'custom-height-badge',
          description: 'Badge with custom height for special layouts',
          label: 'Custom Height',
          hint: '28px height with custom color',
          props: {
            height: 28,
            color: 'blue-500',
            textColor: 'white'
          },
          content: 'Featured',
          code: `yz-badge(height="28" color="blue-500" text-color="white") Featured`
        }
      ]
    },
    sizeVariations() {
      return [
        {
          name: 'Small Badge',
          id: 'small-badge',
          description: 'Compact badge for tight spaces (20px height)',
          label: 'Small Badge (sm)',
          hint: '20px height, 2px 4px padding',
          props: {
            size: 'sm'
          },
          content: 'Small',
          code: `yz-badge(size="sm") Small`
        },
        {
          name: 'Medium Badge',
          id: 'medium-badge',
          description: 'Standard badge size for general use (24px height)',
          label: 'Medium Badge (md)',
          hint: '24px height, 2px 4px padding',
          props: {
            size: 'md'
          },
          content: 'Medium',
          code: `yz-badge(size="md") Medium`
        }
      ]
    },
    colorVariations() {
      return [
        {
          name: 'Design Token Colors',
          id: 'design-token-colors',
          description: 'Using design system color tokens',
          label: 'Design Token Colors',
          hint: 'yz-color-name format',
          badges: [
            { label: 'Success', props: { color: 'success-500', textColor: 'white' } },
            { label: 'Warning', props: { color: 'warning-500', textColor: 'white' } },
            { label: 'Danger', props: { color: 'danger-500', textColor: 'white' } },
            { label: 'Info', props: { color: 'blue-500', textColor: 'white' } }
          ],
          code: `yz-badge(color="success-500" text-color="white") Success
yz-badge(color="warning-500" text-color="white") Warning
yz-badge(color="danger-500" text-color="white") Danger
yz-badge(color="blue-500" text-color="white") Info`
        },
        {
          name: 'Custom Hex Colors',
          id: 'custom-hex-colors',
          description: 'Using custom hex color values',
          label: 'Custom Hex Colors',
          hint: 'Direct hex values',
          badges: [
            { label: 'Purple', props: { color: '#8B5CF6', textColor: '#FFFFFF' } },
            { label: 'Pink', props: { color: '#EC4899',  textColor: '#FFFFFF' } },
            { label: 'Teal', props: { color: '#14B8A6',  textColor: '#FFFFFF' } }
          ],
          code: `yz-badge(color="#8B5CF6" text-color="#FFFFFF") Purple
yz-badge(color="#EC4899" text-color="#FFFFFF") Pink
yz-badge(color="#14B8A6" text-color="#FFFFFF") Teal`
        },
        {
          name: 'Text Colors',
          id: 'text-colors',
          description: 'Customizing text color with different backgrounds',
          label: 'Text Colors',
          hint: 'textColor prop',
          badges: [
            { label: 'Light BG', props: { color: 'neutral-200', textColor: 'neutral-700' } },
            { label: 'Dark BG', props: { color: 'neutral-700', textColor: 'white' } },
            { label: 'Custom', props: { color: '#FFE4B5', textColor: '#8B4513' } }
          ],
          code: `yz-badge(color="neutral-200" text-color="neutral-700") Light BG
yz-badge(color="neutral-700" text-color="white") Dark BG
yz-badge(color="#FFE4B5" text-color="#8B4513") Custom`
        }
      ]
    },
    heightVariations() {
      return [
        {
          name: 'Custom Height Numbers',
          id: 'custom-height-numbers',
          description: 'Using numbers for height (converted to px)',
          label: 'Number Heights',
          hint: 'Numbers converted to px',
          badges: [
            { label: '16px', props: { height: 16, color: 'blue-500', textColor: 'white' } },
            { label: '20px', props: { height: 20, color: 'success-500', textColor: 'white' } },
            { label: '28px', props: { height: 28, color: 'warning-500', textColor: 'white' } },
            { label: '32px', props: { height: 32, color: 'danger-500', textColor: 'white' } }
          ],
          code: `yz-badge(height="16" color="blue-500" text-color="white") 16px
yz-badge(height="20" color="success-500" text-color="white") 20px
yz-badge(height="28" color="warning-500" text-color="white") 28px
yz-badge(height="32" color="danger-500" text-color="white") 32px`
        },
        {
          name: 'Custom Height Strings',
          id: 'custom-height-strings',
          description: 'Using string values for height with units',
          label: 'String Heights',
          hint: 'String values with units',
          badges: [
            { label: '1.5rem', props: { height: '1.5rem', color: 'primary', textColor: 'white' } },
            { label: '2rem', props: { height: '2rem', color: 'blue-500', textColor: 'white' } },
            { label: '2.5rem', props: { height: '2.5rem', color: 'success-500', textColor: 'white' } }
          ],
          code: `yz-badge(height="1.5rem" color="primary" text-color="white") 1.5rem
yz-badge(height="2rem" color="blue-500" text-color="white") 2rem
yz-badge(height="2.5rem" color="success-500" text-color="white") 2.5rem`
        },
        {
          name: 'Height with Size Comparison',
          id: 'height-size-comparison',
          description: 'Comparing custom height with default sizes',
          label: 'Height vs Size',
          hint: 'Custom height overrides size',
          badges: [
            { label: 'sm (20px)', props: { size: 'sm', color: 'neutral-300', textColor: 'neutral-700' } },
            { label: 'md (24px)', props: { size: 'md', color: 'neutral-300', textColor: 'neutral-700' } },
            { label: 'Custom 30px', props: { height: 30, color: 'primary', textColor: 'white' } },
            { label: 'Custom 18px', props: { height: 18, color: 'warning-500', textColor: 'white' } }
          ],
          code: `yz-badge(size="sm" color="neutral-300" text-color="neutral-700") sm (20px)
yz-badge(size="md" color="neutral-300" text-color="neutral-700") md (24px)
yz-badge(height="30" color="primary" text-color="white") Custom 30px
yz-badge(height="18" color="warning-500" text-color="white") Custom 18px`
        },
        {
          name: 'Height with Different Content',
          id: 'height-content-variations',
          description: 'How custom height works with various content types',
          label: 'Height Content Variations',
          hint: 'Height adapts to content',
          badges: [
            { label: 'Short', props: { height: 26, color: 'blue-500', textColor: 'white' } },
            { label: 'Medium Text', props: { height: 26, color: 'success-500', textColor: 'white' } },
            { label: 'Longer Content', props: { height: 26, color: 'warning-500', textColor: 'white' } },
            { label: '99+', props: { height: 26, color: 'danger-500', textColor: 'white' } }
          ],
          code: `yz-badge(height="26" color="blue-500" text-color="white") Short
yz-badge(height="26" color="success-500" text-color="white") Medium Text
yz-badge(height="26" color="warning-500" text-color="white") Longer Content
yz-badge(height="26" color="danger-500" text-color="white") 99+`
        }
      ]
    },
    borderVariations() {
      return [
        {
          name: 'Basic Border',
          id: 'basic-border',
          description: 'Badge with default border styling',
          label: 'Basic Border',
          hint: 'border prop enabled',
          props: {
            border: true,
            color: 'neutral-100'
          },
          content: 'Bordered',
          code: `yz-badge(border color="neutral-100") Bordered`
        },
        {
          name: 'Custom Border Color',
          id: 'custom-border-color',
          description: 'Badge with custom border color',
          label: 'Custom Border Color',
          hint: 'borderColor prop',
          props: {
            border: true,
            color: 'red-100',
            textColor: 'danger-500',
            borderColor: 'danger-500'
          },
          content: 'Custom Border',
          code: `yz-badge(border color="red-100" text-color="danger-500" border-color="danger-500") Custom Border`
        },
      ]
    },
    closableVariations() {
      return [
        {
          name: 'Basic Closable Badges',
          id: 'basic-closable',
          description: 'Basic badges with close functionality',
          label: 'Basic Closable',
          hint: 'Click X to close',
          badges: [
            { label: 'Tag 1', props: { closable: true, color: 'blue-500', textColor: 'white' } },
            { label: 'Tag 2', props: { closable: true, color: 'success-500', textColor: 'white' } },
            { label: 'Tag 3', props: { closable: true, color: 'warning-500', textColor: 'white' } },
            { label: 'Tag 4', props: { closable: true, color: 'danger-500', textColor: 'white' } }
          ],
          code: `yz-badge(closable color="blue-500" text-color="white" @close="handleClose") Tag 1
yz-badge(closable color="success-500" text-color="white" @close="handleClose") Tag 2
yz-badge(closable color="warning-500" text-color="white" @close="handleClose") Tag 3
yz-badge(closable color="danger-500" text-color="white" @close="handleClose") Tag 4`
        },
        {
          name: 'Closable with Different Sizes',
          id: 'closable-sizes',
          description: 'Closable badges in different sizes',
          label: 'Closable Sizes',
          hint: 'Close button adapts to badge size',
          badges: [
            { label: 'Small', props: { closable: true, size: 'sm', color: 'primary', textColor: 'white' } },
            { label: 'Medium', props: { closable: true, size: 'md', color: 'primary', textColor: 'white' } },
            { label: 'Custom Height', props: { closable: true, height: 32, color: 'blue-500', textColor: 'white' } }
          ],
          code: `yz-badge(closable size="sm" color="primary" text-color="white" @close="handleClose") Small
yz-badge(closable size="md" color="primary" text-color="white" @close="handleClose") Medium
yz-badge(closable height="32" color="blue-500" text-color="white" @close="handleClose") Custom Height`
        },
        {
          name: 'Closable Tags',
          id: 'closable-tags',
          description: 'Common use case for tag management',
          label: 'Tag Management',
          hint: 'Typical tag removal interface',
          badges: [
            { label: 'JavaScript', props: { closable: true, color: 'neutral-200', textColor: 'neutral-700', border: true } },
            { label: 'Vue.js', props: { closable: true, color: 'success-100', textColor: 'success-700' } },
            { label: 'Frontend', props: { closable: true, color: 'blue-100', textColor: 'blue-700' } },
            { label: 'Design System', props: { closable: true, color: 'warning-100', textColor: 'warning-700' } }
          ],
          code: `yz-badge(closable color="neutral-200" text-color="neutral-700" border @close="removeTag") JavaScript
yz-badge(closable color="success-100" text-color="success-700" @close="removeTag") Vue.js
yz-badge(closable color="blue-100" text-color="blue-700" @close="removeTag") Frontend
yz-badge(closable color="warning-100" text-color="warning-700" @close="removeTag") Design System`
        },
        {
          name: 'Closable with Custom Styling',
          id: 'closable-custom',
          description: 'Closable badges with custom padding and radius',
          label: 'Custom Closable',
          hint: 'Custom styling with close functionality',
          badges: [
            { label: 'Rounded', props: { closable: true, radius: '12px', color: 'primary', textColor: 'white', padding: '6px 12px' } },
            { label: 'Pill', props: { closable: true, radius: '20px', color: 'success-500', textColor: 'white', padding: '8px 16px' } },
            { label: 'Square', props: { closable: true, radius: '4px', color: 'danger-500', textColor: 'white', padding: '4px 8px' } }
          ],
          code: `yz-badge(closable radius="12px" color="primary" text-color="white" padding="6px 12px" @close="handleClose") Rounded
yz-badge(closable radius="20px" color="success-500" text-color="white" padding="8px 16px" @close="handleClose") Pill
yz-badge(closable radius="4px" color="danger-500" text-color="white" padding="4px 8px" @close="handleClose") Square`
        }
      ]
    },
    stylingVariations() {
      return [
        {
          name: 'Custom Padding',
          id: 'custom-padding',
          description: 'Badge with custom padding using CSS classes',
          label: 'Custom Padding',
          hint: 'class-based padding',
          props: {
            color: 'primary'
          },
          content: 'Padded',
          code: `yz-badge.px-5.py-2(color="primary") Padded`
        },
        {
          name: 'Custom Radius',
          id: 'custom-radius',
          description: 'Badge with custom border radius for different rounded corners',
          label: 'Custom Radius',
          hint: 'radius prop',
          props: {
            radius: '12px',
            color: 'blue-500',
            textColor: 'white'
          },
          content: 'Rounded',
          code: `yz-badge(radius="12px" color="blue-500" text-color="white") Rounded`
        },
        {
          name: 'Combined Styling',
          id: 'combined-styling',
          description: 'Badge with both custom padding and radius',
          label: 'Combined Styling',
          hint: 'Multiple custom props',
          props: {
            padding: '8px 16px',
            radius: '20px',
            color: 'success-500',
            textColor: 'white'
          },
          content: 'Custom',
          code: `yz-badge(padding="8px 16px" radius="20px" color="success-500" text-color="white") Custom`
        },
        {
          name: 'Closable with Styling',
          id: 'closable-styling',
          description: 'Closable badge with custom styling combination',
          label: 'Closable Styling',
          hint: 'Combines height, padding, and closable',
          props: {
            closable: true,
            height: 30,
            padding: '6px 12px',
            radius: '15px',
            color: 'warning-500',
            textColor: 'white'
          },
          content: 'Removable',
          code: `yz-badge(closable height="30" padding="6px 12px" radius="15px" color="warning-500" text-color="white" @close="handleClose") Removable`
        }
      ]
    },
    slotVariations() {
      return [
        {
          name: 'Icon Badges',
          id: 'icon-badges',
          description: 'Using yz-icon components inside badges',
          label: 'Icon Badges',
          hint: 'yz-icon in default slot',
          badges: [
            { 
              id: 'star-icon',
              props: { color: 'warning-500', textColor: 'white', padding: '4px 6px' },
              slotType: 'icon',
              icon: '$yzi-star-01',
              iconColor: 'white'
            },
            { 
              id: 'check-icon',
              props: { color: 'success-500', textColor: 'white', padding: '4px 6px' },
              slotType: 'icon',
              icon: '$yzi-check',
              iconColor: 'white'
            },
            { 
              id: 'close-icon',
              props: { color: 'danger-500', textColor: 'white', padding: '4px 6px' },
              slotType: 'icon',
              icon: '$yzi-image-user-x',
              iconColor: 'white'
            }
          ],
          code: `yz-badge(color="warning-500" text-color="white" padding="4px 6px")
  yz-icon(size="12" color="white") $yzi-star-01

yz-badge(color="success-500" text-color="white" padding="4px 6px")
  yz-icon(size="12" color="white") $yzi-check

yz-badge(color="danger-500" text-color="white" padding="4px 6px")
  yz-icon(size="12" color="white") $yzi-image-user-x`
        },
        {
          name: 'Dot Badges',
          id: 'dot-badges',
          description: 'Using yz-dot components for status indicators',
          label: 'Dot Badges',
          hint: 'yz-dot in default slot',
          badges: [
            { 
              id: 'online-dot',
              props: { color: 'neutral-300', textColor: 'neutral-700', padding: '6px 8px' },
              slotType: 'dot',
              dotColor: 'success-500',
              dotSize: '6px'
            },
            { 
              id: 'away-dot',
              props: { color: 'neutral-100', textColor: 'neutral-700', padding: '6px 8px', border: true },
              slotType: 'dot',
              dotColor: 'warning-500',
              dotSize: '6px'
            },
            { 
              id: 'offline-dot',
              props: { color: 'neutral-100', textColor: 'neutral-700', padding: '6px 8px', border: true },
              slotType: 'dot',
              dotColor: 'neutral-400',
              dotSize: '6px'
            }
          ],
          code: `yz-badge(color="neutral-300" text-color="neutral-700" padding="6px 8px")
  yz-dot(color="success-500" size="6px")

yz-badge(color="neutral-100" text-color="neutral-700" padding="6px 8px" border)
  yz-dot(color="warning-500" size="6px")

yz-badge(color="neutral-100" text-color="neutral-700" padding="6px 8px" border)
  yz-dot(color="neutral-400" size="6px")`
        },
        {
          name: 'Mixed Content',
          id: 'mixed-content',
          description: 'Combining dots with text in badges',
          label: 'Mixed Content',
          hint: 'yz-dot + text',
          badges: [
            { 
              id: 'online-status',
              props: { color: 'neutral-100', textColor: 'neutral-700', padding: '6px 10px', border: true },
              slotType: 'mixed',
              dotColor: 'success-500',
              dotSize: '6px',
              text: 'Online'
            },
            { 
              id: 'busy-status',
              props: { color: 'red-100', textColor: 'danger-500', padding: '6px 10px', border: false },
              slotType: 'mixed',
              dotColor: 'danger-500',
              dotSize: '6px',
              text: 'Busy'
            },
            { 
              id: 'away-status',
              props: { color: 'neutral-100', textColor: 'neutral-700', padding: '6px 10px', border: true },
              slotType: 'mixed',
              dotColor: 'warning-500',
              dotSize: '6px',
              text: 'Away'
            }
          ],
          code: `yz-badge(color="neutral-100" text-color="neutral-700" padding="6px 10px" border)
  yz-dot(color="success-500" size="6px")
  span.ml-1 Online

yz-badge(color="red-100" text-color="danger-500" padding="6px 10px")
  yz-dot(color="danger-500" size="6px")
  span.ml-1 Busy

yz-badge(color="neutral-100" text-color="neutral-700" padding="6px 10px" border)
  yz-dot(color="warning-500" size="6px")
  span.ml-1 Away`
        }
      ]
    },
    componentProps() {
      return [
        {
          name: 'size',
          type: 'String',
          default: 'sm',
          description: 'Size of the badge. Options: sm (20px height), md (24px height)'
        },
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Background color. Supports primary, design tokens (e.g., success-500), or hex values'
        },
        {
          name: 'border',
          type: 'Boolean',
          default: 'false',
          description: 'Whether to show border around the badge'
        },
        {
          name: 'borderColor',
          type: 'String',
          default: 'null',
          description: 'Border color when border is enabled. Supports design tokens or hex values'
        },
        {
          name: 'textColor',
          type: 'String',
          default: 'neutral-500',
          description: 'Text color of the badge content. Supports design tokens or hex values'
        },
        {
          name: 'radius',
          type: 'String | Number',
          default: '4px',
          description: 'Border radius of the badge. Accepts px values as string or numbers'
        },
        {
          name: 'height',
          type: 'String | Number',
          default: 'null',
          description: 'Custom height of the badge. Numbers are converted to px. Overrides default size-based heights'
        },
        {
          name: 'closable',
          type: 'Boolean',
          default: 'false',
          description: 'Whether to show a close button. Emits close event when clicked'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'default',
          description: 'Main content of the badge (text, numbers, or other inline elements)'
        }
      ]
    },
    componentEvents() {
      return [
        {
          name: 'close',
          payload: 'Event',
          description: 'Emitted when the close button is clicked (only when closable=true)'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'Status indicators',
          approach: 'Use color-coded badges with clear, short text',
          example: 'yz-badge(color="success-500") Active'
        },
        {
          scenario: 'Notification counts',
          approach: 'Use danger color for urgency, keep numbers concise',
          example: 'yz-badge(color="danger-500") 5'
        },
        {
          scenario: 'Category labels',
          approach: 'Use neutral or brand colors, keep text brief',
          example: 'yz-badge(color="blue-500") Tech'
        },
        {
          scenario: 'Feature flags',
          approach: 'Use bordered badges for subtle indicators',
          example: 'yz-badge(border color="neutral-100") Beta'
        },
        {
          scenario: 'Tag management',
          approach: 'Use closable badges for removable tags',
          example: 'yz-badge(closable @close="removeTag") JavaScript'
        },
        {
          scenario: 'Custom heights',
          approach: 'Use height prop for specific design requirements',
          example: 'yz-badge(height="32") Large Badge'
        },
        {
          scenario: 'Filter chips',
          approach: 'Use closable with custom styling for filter interfaces',
          example: 'yz-badge(closable border radius="20px") Active Filter'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    handleBadgeClose(label) {
      console.log(`Badge "${label}" was closed`)
      // In a real application, you would handle removing the badge from your data
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}
</style>